import React, { useState, useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import axios from 'axios'
import { get as _get } from 'lodash'
import queryString from 'query-string'

import KlassForm from '../leevo_ui/KlassForm/KlassForm'
import ApplicationLayout from '../leevo_ui/ApplicationLayout/ApplicationLayout'
import { requestCreator } from '../../utilities/requests'
import { mapKlassPayload } from '../../utilities/apiPayloadMappers'
import { datesFromKlassForCopy } from '../../utilities/helpers'
import useFacility from '../../utilities/hooks/useFacility'

function FacilitiesKlassesNew() {
  const history = useHistory()
  const { facility, isLoading: isFacilityLoading } = useFacility()
  const [isLoading, setIsLoading] = useState(true)
  const [dates, setDates] = useState([])
  const [coaches, setCoaches] = useState([])
  const [klassPackages, setKlassPackages] = useState([])
  const [supportedSkillLevels, setSupportedSkillLevels] = useState([])
  const [students, setStudents] = useState([])
  const [maxStudents, setMaxStudents] = useState(null)
  const [initialSport, setInitialSport] = useState('skating')
  const [initialCurriculumId, setInitialCurriculumId] = useState(null)
  const { search } = useLocation()
  const { get: getSkillLevels, cancel: cancelSkillLevelsRequests } = requestCreator()
  const { get: getCoaches, cancel: cancelCoachesRequests } = requestCreator()
  const { get: getKlass, post: createKlass, cancel: cancelKlassRequests } = requestCreator()
  const { get: getKlassPackages, cancel: cancelKlassPackagesRequests } = requestCreator()

  const { copy_id: copyId } = queryString.parse(search)

  function generateEmptyKlassValues() {
    const defaultCurriculumId = facility.curriculums?.[0]?.id
    const skillLevelsUrl = defaultCurriculumId
      ? `/api/facilities/${facility.id}/skill_levels?curriculum_id=${defaultCurriculumId}`
      : `/api/facilities/${facility.id}/skill_levels`

    getSkillLevels(skillLevelsUrl)
      .then((skillLevels) =>
        setSupportedSkillLevels(
          skillLevels.map((skillLevel) => ({
            ...skillLevel,
            label: skillLevel.name,
            value: skillLevel.id
          }))
        )
      )
      .then(() => getCoaches(`/api/facilities/${facility.id}/coaches`))
      .then((coachList) =>
        setCoaches(
          coachList.map((coach) => ({
            ...coach,
            label: coach.fullName || `${coach.email} (pending)`, // set a label for compatibility with multi-select searching
            value: coach.id
          }))
        )
      )
      .then(() => getKlassPackages(`/api/facilities/${facility.id}/class_packages`))
      .then((klassPackages) =>
        setKlassPackages(
          klassPackages.map((klassPackage) => ({
            ...klassPackage,
            label: klassPackage.title,
            value: klassPackage.id,
            selected: false
          }))
        )
      )
      .then(() => setIsLoading(false))
      .catch((error) => {
        if (!axios.isCancel(error)) {
          history.push('/500')
        }
      })
  }

  function generatePopulatedKlassValues() {
    let klass
    getKlass(`/api/facilities/${facility.id}/klasses/${copyId}`, {
      params: { include: 'klass_days,students,students.current_skill_level' }
    })
      .then((retrievedKlass) => {
        klass = retrievedKlass
        setDates(datesFromKlassForCopy(klass, facility.timeZone))
        setStudents(klass.students.map((student) => ({ ...student, selected: false })))
        setMaxStudents(klass.maxStudents)
        setInitialSport(klass.sport || facility.curriculums?.[0]?.sport || 'skating')
        setInitialCurriculumId(klass.curriculumId || null)
      })
      .then(() => {
        const curriculumId = klass.curriculumId || facility.curriculums?.[0]?.id
        const skillLevelsUrl = curriculumId
          ? `/api/facilities/${facility.id}/skill_levels?curriculum_id=${curriculumId}`
          : `/api/facilities/${facility.id}/skill_levels`
        return getSkillLevels(skillLevelsUrl)
      })
      .then((skillLevels) =>
        setSupportedSkillLevels(
          skillLevels.map((skillLevel) => ({
            ...skillLevel,
            label: skillLevel.name,
            value: skillLevel.id,
            selected: klass.supportedSkillLevels.map((s) => s.id).includes(skillLevel.id)
          }))
        )
      )
      .then(() => getCoaches(`/api/facilities/${facility.id}/coaches`))
      .then((coachList) =>
        setCoaches(
          coachList.map((coach) => ({
            ...coach,
            label: coach.fullName || `${coach.email} (pending)`, // set a label for compatibility with multi-select searching
            value: coach.id,
            selected: klass.coaches.map((c) => c.id).includes(coach.id)
          }))
        )
      )
      .then(() => getKlassPackages(`/api/facilities/${facility.id}/class_packages`))
      .then((klassPackages) =>
        setKlassPackages(
          klassPackages.map((klassPackage) => ({
            ...klassPackage,
            label: klassPackage.title,
            value: klassPackage.id,
            selected: klass.klassPackages.map((s) => s.id).includes(klassPackage.id)
          }))
        )
      )
      .then(() => setIsLoading(false))
      .catch((error) => {
        if (!axios.isCancel(error)) {
          history.push('/500')
        }
      })
  }

  useEffect(() => {
    if (!isFacilityLoading && facility?.id) {
      copyId ? generatePopulatedKlassValues() : generateEmptyKlassValues()
    }

    return () => {
      cancelSkillLevelsRequests()
      cancelCoachesRequests()
      cancelKlassRequests()
      cancelKlassPackagesRequests()
    }
  }, [isFacilityLoading, facility?.id, copyId])

  function onSubmit(values) {
    setDates(values.dates)
    setCoaches(
      coaches.map((coach) => ({
        ...coach,
        selected: values.coaches.includes(coach)
      }))
    )
    setSupportedSkillLevels(
      supportedSkillLevels.map((skillLevel) => ({
        ...skillLevel,
        selected: values.supportedSkillLevels.includes(skillLevel)
      }))
    )
    setMaxStudents(values.maxStudents)

    const klassData = {
      ...values,
      coaches: values.coaches.map((coach) => coach.id),
      klassPackages: values.klassPackages
        .filter((klassPackage) => klassPackage.selected)
        .map((klassPackage) => klassPackage.id),
      dates: values.dates.filter(({ isTimeSet }) => isTimeSet).map(({ date }) => date.toString()),
      students: values.students.filter((student) => student.selected).map((student) => student.id)
    }

    if (values.curriculumMode === 'custom' && values.customSkillLevels?.length > 0) {
      delete klassData.supportedSkillLevels
      klassData.sport = values.sport
      klassData.customSkillLevels = values.customSkillLevels.map((level) => ({
        name: level.name,
        skills: level.skills?.map((skill) => ({ name: skill.name })) || []
      }))
    } else {
      klassData.supportedSkillLevels = values.supportedSkillLevels.map((sl) => sl.id)
    }

    return createKlass(`/api/facilities/${facility.id}/klasses`, { klass: mapKlassPayload(klassData) })
      .then(() =>
        history.push({
          pathname: `/facilities/${facility.id}/classes`,
          state: {
            notification: {
              message: 'Class created successfully!',
              color: 'green'
            }
          }
        })
      )
      .catch((error) => {
        if (!axios.isCancel(error)) {
          const message = _get(error, 'response.data.error') || 'We were unable to save your changes.'
          history.replace({
            state: {
              notification: {
                message,
                color: 'red'
              }
            }
          })
        }
      })
  }

  const defaultCurriculum = facility.curriculums?.[0]

  return (
    <ApplicationLayout isContentLoading={isLoading}>
      <KlassForm
        dates={dates}
        coaches={coaches}
        students={students}
        klassPackages={klassPackages}
        supportedSkillLevels={supportedSkillLevels}
        maxStudents={maxStudents}
        onSubmit={onSubmit}
        initialSport={copyId ? initialSport : defaultCurriculum?.sport || 'skating'}
        initialCurriculumId={copyId ? initialCurriculumId : defaultCurriculum?.id || null}
      />
    </ApplicationLayout>
  )
}

export default FacilitiesKlassesNew
